/**
 * Algebra Kingdom, surveyed: the village and the inside of all five of its
 * regions (docs/rpg.md §22).
 *
 * **The geography is the balance.** One road runs the whole length of the
 * kingdom, and the Equation Forest sits astride it with *the same ground on
 * both sides* — the same trees, the same ride, the same turn — because the only
 * move this kingdom's mathematics allows is the one you make to both sides at
 * once. Everything else is arranged by what it does to that road: the letters
 * are in the west where you start naming things, the marsh lies south where the
 * ground is never exactly level, the long fields open north where an expression
 * can run on and on, and the road ends at a castle with two gates.
 *
 * ```
 *   .  meadow     #  road / lane      ~  water     _  floor
 *   f  trees      "  crop             ^  crag      %  wall
 * ```
 *
 * **A place is a reference and never a second name.** A region place carries a
 * region id and nothing else; the name, the emoji and the progress on its plate
 * are read off the kingdom, so a plate and the card below it cannot disagree.
 * Only the notice board and the ways out carry their own words, because nothing
 * in the graph names them.
 *
 * **Terrain is scenery and never a lock.** The mere, the crags and every castle
 * wall stop a step and nothing else: `validateTown` and `validateRegionMap`
 * walk all six of these from their arrival tiles on every
 * `npm run content:validate` and fail the build if one place is stranded. Every
 * region and every concept here is an ordinary card on the kingdom page too —
 * the map is the picture, the cards are the index.
 */

import type { RegionMap, Town } from '@yuan-math/world';

const ALGEBRA = 'kingdom-algebra';

/* -------------------------------------------------------------------------- */
/* The kingdom                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The kingdom road, and the five places it passes.
 *
 * The road is the spine and the Equation Forest is the balance point on it: the
 * wood north of the road is the wood south of the road, tree for tree and turn
 * for turn, and the ride through it loops out and back on either side. A
 * student who walks the northern loop and then the southern one has done the
 * same thing to both sides.
 *
 * West of the wood the ground is human scale — four cottages with a letter on
 * each door, up a short lane off the road. South-west it goes wet: the marsh
 * has no level ground on it and its signpost is at the end of a spit between
 * two pools. North-east the fields run long and straight, which is what a
 * polynomial looks like before anyone folds it. And the road *ends*, at the
 * gate of a castle with two ways in and one keep behind them — the Quadratic
 * King is in that keep, and the castle's own signpost is the last tile of the
 * road.
 */
const ALGEBRA_TOWN: Town = {
  kingdomId: ALGEBRA,
  caption:
    'One road the length of the kingdom: cottages with letters on the doors, a wood that is the same on both sides of it, the marsh below, the long fields above, and a castle with two gates at the end.',
  width: 26,
  height: 18,
  /* On the road by the notice board, a few steps in from the way home. The
     village lane is in sight to the north and the marsh path to the south,
     which is the answer to "where am I" that needs no walking first. */
  start: { x: 4, y: 9 },
  rows: [
    'f^^^^^^^^^^^^^^^^^^^^^^^^^',
    'f..f...f...ffffff...f...f^',
    'f.%%%.%%%f.ffffff."""""""^',
    'f.%_%.%_%.fffffff.......f^',
    'f....f.....ffffff."""""""^',
    'ff...#...f.fffffff......f^',
    'f.%%%#%%%..ff###f."""""""^',
    'f.%_%#%_%..ff#f#f.......f^',
    'ff#..#.....ff#f#f......ff^',
    'f########################^',
    'ff....#....ff#f#f.%_%%%_%^',
    'f~~...#.~~.ff#f#f.%_____%^',
    'f~.f....~~.ff###ff%_%%%_%^',
    'f~.~.~.~~..ffffff.%_%_%_%^',
    'f~.~~~.~..fffffff.%_%_%_%^',
    'f~...~...f.ffffff.%_____%^',
    'f~~.~~.~~~.ffffff.%%%%%%%^',
    'fffffffffffffffffffffffff^',
  ],
  /**
   * Eight places, west to east along the road. Each region stands at the end of
   * its own lane, spit or field row rather than on the road itself, so walking
   * the kingdom never drops a student into a region they were only passing.
   * The forest signpost is the exception and is safe for the same reason the
   * coast road is: the loop it stands on has the road as its way round.
   */
  places: [
    { kind: 'road', name: 'The Road Home', emoji: '🗺️', x: 1, y: 9 },
    { kind: 'quests', name: 'The Notice Board', emoji: '📜', x: 2, y: 8 },
    { kind: 'region', regionId: 'region-variable-village', x: 5, y: 5 },
    { kind: 'region', regionId: 'region-inequality-marsh', x: 4, y: 13 },
    { kind: 'region', regionId: 'region-equation-forest', x: 14, y: 6 },
    { kind: 'region', regionId: 'region-polynomial-plains', x: 24, y: 4 },
    { kind: 'region', regionId: 'region-quadratic-castle', x: 24, y: 9 },
    { kind: 'boss', bossId: 'boss-algebra-kingdom', x: 21, y: 13 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Variable Village                                                           */
/* -------------------------------------------------------------------------- */

/**
 * One street, four cottages with a letter on the door, and a tower at the top.
 *
 * The street runs up from the gate and the mathematics runs up with it: the
 * cottages nearest the gate are where a letter first stands for a number, the
 * pair above them are where letters get written into expressions, and the house
 * at the far end is where two columns of numbers turn out to move together.
 *
 * The tower is the one place where the picture is the argument. Exponents are
 * on the floor below their own rules, and the only way to the upper floor is
 * the stair out of the lower one — because a rule about powers is a storey
 * built on top of knowing what a power is, and there is no other way up.
 */
const VARIABLE_VILLAGE: RegionMap = {
  kingdomId: ALGEBRA,
  regionId: 'region-variable-village',
  caption: 'A street of cottages with a letter on every door, and a tower at the top of it with its upper floor built on the one below.',
  width: 18,
  height: 14,
  /* On the street between the first two cottages, facing up the village. */
  start: { x: 8, y: 11 },
  rows: [
    'ffffffffffffffffff',
    'f.....%%%%%......f',
    'f.f.f.%___%.%%%%%f',
    'f.....%%%_%.____%f',
    'f.f...%___%.%%%%%f',
    'f.f...%%_%%......f',
    'f.f.....#......f.f',
    'f%%%%%..#..%%%%%.f',
    'f%____..#..____%.f',
    'f%%%%%..#..%%%%%.f',
    'f%%%%%..#..%%%%%.f',
    'f%____..#..____%.f',
    'f%%%%%..#..%%%%%.f',
    'ffffffff#fffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'variables', x: 3, y: 11 },
    { kind: 'concept', conceptId: 'numerical-expressions', x: 13, y: 11 },
    { kind: 'concept', conceptId: 'algebraic-expressions', x: 3, y: 8 },
    { kind: 'concept', conceptId: 'linear-expressions', x: 13, y: 8 },
    { kind: 'concept', conceptId: 'number-pattern-pairs', x: 14, y: 3 },
    { kind: 'concept', conceptId: 'exponents', x: 7, y: 4 },
    { kind: 'concept', conceptId: 'exponent-rules', x: 7, y: 2 },
    { kind: 'road', name: 'The Village Gate', emoji: '⚔️', x: 8, y: 13 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Equation Forest                                                            */
/* -------------------------------------------------------------------------- */

/**
 * A wood with a ride down the middle and the same clearing on either side of it.
 *
 * This map is drawn as an exact mirror about the ride, and that is the whole of
 * what an equation is: whatever is west of the line is east of it too, and the
 * only way to keep it that way is to do the same thing to both. The three pairs
 * of clearings are paired on purpose — one step opposite many steps, fractions
 * opposite letters, a proportion opposite the two-variable relationship that is
 * the same statement drawn as a line.
 *
 * The seventh concept is the ride's own head, where the two halves meet: what a
 * solution *is* is the one thing on this map that does not have a twin.
 */
const EQUATION_FOREST: RegionMap = {
  kingdomId: ALGEBRA,
  regionId: 'region-equation-forest',
  caption: 'A ride down the middle of the wood, with the same three clearings either side of it. Whatever you do on one side has already been done on the other.',
  width: 19,
  height: 13,
  /* On the ride, one clearing in from the way out, with the wood either side. */
  start: { x: 9, y: 10 },
  rows: [
    'fffffffffffffffffff',
    'fffffffff#fffffffff',
    'ffff...ff#ff...ffff',
    'ffff...#####...ffff',
    'ffff...ff#ff...ffff',
    'ffff...ff#ff...ffff',
    'ffff...#####...ffff',
    'ffff...ff#ff...ffff',
    'ffff...ff#ff...ffff',
    'ffff...#####...ffff',
    'ffff...ff#ff...ffff',
    'fffffffff#fffffffff',
    'fffffffff#fffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'linear-equation-solutions', x: 9, y: 1 },
    { kind: 'concept', conceptId: 'one-step-equations', x: 5, y: 3 },
    { kind: 'concept', conceptId: 'multi-step-equations', x: 13, y: 3 },
    { kind: 'concept', conceptId: 'equations-with-fractions', x: 5, y: 6 },
    { kind: 'concept', conceptId: 'literal-equations', x: 13, y: 6 },
    { kind: 'concept', conceptId: 'proportional-relationships', x: 5, y: 9 },
    { kind: 'concept', conceptId: 'two-variable-relationships', x: 13, y: 9 },
    { kind: 'road', name: 'The Forest Ride', emoji: '⚔️', x: 9, y: 12 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Inequality Marsh                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Firm ground in stretches, and open water everywhere else.
 *
 * Nothing on this map is a point. The lane across the middle is one long strip
 * with a concept at *each* end, because that is what an answer looks like here;
 * the west bank is a spit with a pool on both sides of it, which is a compound
 * inequality drawn rather than written; and the east side is not a path at all
 * but a whole flat of open ground, because two variables give you an area.
 *
 * Absolute value stands at the head of the channel, one step off the lane, with
 * the same water either side of it — the two directions away from it are the
 * same distance.
 */
const INEQUALITY_MARSH: RegionMap = {
  kingdomId: ALGEBRA,
  regionId: 'region-inequality-marsh',
  caption: 'A causeway in, a firm lane with an end at each end of it, a spit between two pools, and a flat too wide to call a path.',
  width: 17,
  height: 12,
  /* On the causeway, with the lane and the whole marsh ahead. */
  start: { x: 8, y: 2 },
  rows: [
    '~~~~~~~~#~~~~~~~~',
    '~~~~~~~~#~.~.~~~~',
    '~~~~~~~~#....~~~~',
    '~~~###########~~~',
    '~~~~~~.~.~~.~~~~~',
    '~~~~~~.~~~.....~~',
    '~~~~~~.~~~.....~~',
    '~~~~~~.~~~.....~~',
    '~~~~~~.~~~.....~~',
    '~~~~~~.~~~.....~~',
    '~~~~~~.~~~.....~~',
    '~~~~~~~~~~~~~~~~~',
  ],
  places: [
    { kind: 'concept', conceptId: 'one-variable-inequalities', x: 3, y: 3 },
    { kind: 'concept', conceptId: 'inequalities', x: 13, y: 3 },
    { kind: 'concept', conceptId: 'absolute-value', x: 8, y: 4 },
    { kind: 'concept', conceptId: 'compound-inequalities', x: 6, y: 10 },
    { kind: 'concept', conceptId: 'two-variable-inequalities', x: 12, y: 8 },
    /* Dry, wet, dry, wet, dry: the tussocks north of the lane change under
       your feet three times across seven tiles, which is the only thing a
       polynomial inequality ever asks you to find. */
    { kind: 'concept', conceptId: 'polynomial-inequalities', x: 12, y: 1 },
    { kind: 'road', name: 'The Marsh Road', emoji: '⚔️', x: 8, y: 0 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Polynomial Plains                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Plots, and the same plot drawn a second way.
 *
 * The top of the field is sixteen tiles written out in one long row. The plot
 * under it is the identical sixteen folded into eight by two — walk both and
 * they are the same field, which is the entire claim factoring makes. Beside it
 * one plot is cut by hedges into four equal strips.
 *
 * The middle band is two plots stacked with a hedge between them, twice: a
 * numerator over a denominator, and then a pair of them facing each other
 * across a gap. Below that a square plot whose side is the thing you are asked
 * for, a run of plots each built from the one before it, and — at the far
 * corner — a single tile at the top of a stand of trees that widens by one on
 * every row.
 */
const POLYNOMIAL_PLAINS: RegionMap = {
  kingdomId: ALGEBRA,
  regionId: 'region-polynomial-plains',
  caption: 'Sixteen written out long, and the same sixteen folded into eight by two. Below them a fraction bar drawn as a hedge, a square plot, and a stand of trees that grows by one a row.',
  width: 20,
  height: 15,
  /* On the headland inside the field gate, with the plots ahead. */
  start: { x: 9, y: 12 },
  rows: [
    'ffffffffffffffffffff',
    'f."""""""""""""""".f',
    'f..................f',
    'f.""""""""."f"f"f".f',
    'f.""""""""."f"f"f".f',
    'f..................f',
    'f."""""""."""."""..f',
    'f.fffffff.fff.fff..f',
    'f."""""""."""."""..f',
    'f..................f',
    'f."""."""..........f',
    'f."""."".......f.f.f',
    'f.""".".......f.f.ff',
    'f..................f',
    'fffffffff#ffffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'polynomials', x: 2, y: 1 },
    { kind: 'concept', conceptId: 'factoring', x: 5, y: 3 },
    { kind: 'concept', conceptId: 'polynomial-division', x: 11, y: 3 },
    { kind: 'concept', conceptId: 'rational-expressions', x: 5, y: 6 },
    { kind: 'concept', conceptId: 'rational-equations', x: 11, y: 6 },
    { kind: 'concept', conceptId: 'radical-equations', x: 3, y: 11 },
    { kind: 'concept', conceptId: 'recursive-sequences', x: 6, y: 12 },
    { kind: 'concept', conceptId: 'binomial-theorem', x: 16, y: 10 },
    { kind: 'road', name: 'The Field Gate', emoji: '⚔️', x: 9, y: 14 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Quadratic Castle                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Two gates, two wards, two chambers — and one hall behind a pillar.
 *
 * The castle is symmetric about its middle because the king's own boast is that
 * every question worth asking has two answers. Two gates in the south wall open
 * onto two identical wards; the wards run up to two galleries whose far ends are
 * the two ways two conditions can be met at once; and the two chambers off the
 * wards are three by three, because one of them is the room you are asked to
 * make square.
 *
 * The hall in the middle has a pillar in it, and the passage forks around it and
 * joins again at the top. Either side of the pillar arrives at the same tile,
 * which is what a formula that always works actually means — and it is the only
 * tile in the castle that both routes reach.
 */
const QUADRATIC_CASTLE: RegionMap = {
  kingdomId: ALGEBRA,
  regionId: 'region-quadratic-castle',
  caption: 'Two gates, two wards, two chambers — and one hall where the passage forks round a pillar and both ways arrive at the same tile.',
  width: 19,
  height: 13,
  /* Inside, in the south gallery, with both wards and the hall in front. */
  start: { x: 9, y: 8 },
  rows: [
    'f.................f',
    '.%%%%%%%%%%%%%%%%%.',
    '.%______%%%______%.',
    '.%_%%%%%___%%%%%_%.',
    '.%_%___%_%_%___%_%.',
    '.%_%___%_%_%___%_%.',
    '.%_%___%___%___%_%.',
    '.%_%%_%%___%%_%%_%.',
    '.%_______________%.',
    '.%%%%_%%%%%%%_%%%%.',
    '.f...............f.',
    'f.................f',
    'fffffffff#fffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'quadratics', x: 13, y: 5 },
    { kind: 'concept', conceptId: 'completing-the-square', x: 5, y: 5 },
    { kind: 'concept', conceptId: 'quadratic-formula', x: 9, y: 3 },
    { kind: 'concept', conceptId: 'systems-of-equations', x: 7, y: 2 },
    { kind: 'concept', conceptId: 'nonlinear-systems', x: 11, y: 2 },
    { kind: 'road', name: 'The Castle Road', emoji: '⚔️', x: 9, y: 12 },
  ],
};

export { ALGEBRA_TOWN };

/** The five regions of Algebra Kingdom, in the order the road passes them. */
export const ALGEBRA_REGION_MAPS: readonly RegionMap[] = [
  VARIABLE_VILLAGE,
  INEQUALITY_MARSH,
  EQUATION_FOREST,
  POLYNOMIAL_PLAINS,
  QUADRATIC_CASTLE,
];
